import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Plus, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { EmptyState } from '../components/ui/EmptyState';
import { api } from '../lib/api';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type PayrollRun = { id: string; periodMonth: string; status: string; staffCount: number; totalGrossEarnings: string; totalDeductions: string; totalNetPay: string };

const STATUS_STYLE: Record<string, string> = { generating: 'bg-amber-100 text-amber-700', draft: 'bg-slate-100 text-slate-700', generation_failed: 'bg-red-50 text-red-700', locked: 'bg-brand-100 text-brand-700', released: 'bg-emerald-100 text-emerald-700' };
const STATUS_LABEL: Record<string, string> = { generating: 'Generating…', draft: 'Draft', generation_failed: 'Generation failed', locked: 'Locked', released: 'Released' };
const money = (v: string) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const monthLabel = (period: string) => new Date(`${period}T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export function PayrollPage() {
  const client = useQueryClient();
  const [, navigate] = useLocation();
  const { success, error: toastError } = useToast();
  const [month, setMonth] = useState('');

  const runs = useQuery({
    queryKey: ['payroll-runs'],
    queryFn: async () => (await api.get<PayrollRun[]>('/api/payroll-runs')).data,
    refetchInterval: (query) => (query.state.data?.some((r) => r.status === 'generating') ? 2000 : false),
  });

  const create = useMutation({
    mutationFn: () => api.post<PayrollRun>('/api/payroll-runs', { periodMonth: `${month}-01` }),
    onSuccess: ({ data }) => { void client.invalidateQueries({ queryKey: ['payroll-runs'] }); success('Payroll run started — computing in the background'); navigate(`/payroll/${data.id}`); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not start payroll run')),
  });

  const rows = [...(runs.data ?? [])].sort((a, b) => b.periodMonth.localeCompare(a.periodMonth));

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Payroll</h1><p className="mt-1 text-muted">Monthly runs — computed, reviewed, then locked.</p></div>
        <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (month) create.mutate(); }}>
          <label className="label">Period<input type="month" className="field mt-1" required value={month} onChange={(e) => setMonth(e.target.value)} /></label>
          <button className="btn-primary" disabled={create.isPending || !month}><Plus size={18} /> Create run</button>
        </form>
      </div>

      {runs.isLoading && <Loader />}
      {!runs.isLoading && rows.length === 0 && <EmptyState icon={CalendarClock} title="No payroll runs yet" hint="Pick a month above to start the first one." />}

      <div className="space-y-3">
        {rows.map((run) => (
          <Link key={run.id} href={`/payroll/${run.id}`} className="card flex flex-wrap items-center justify-between gap-3 hover:border-brand-500">
            <div className="min-w-0">
              <strong className="flex items-center gap-2">{monthLabel(run.periodMonth)}{run.status === 'generating' && <RefreshCw size={14} className="animate-spin text-muted" />}</strong>
              <p className="mt-1 text-sm text-muted">{run.staffCount} staff{run.status !== 'generating' && ` • Net ${money(run.totalNetPay)}`}</p>
            </div>
            <span className={`badge shrink-0 ${STATUS_STYLE[run.status] ?? ''}`}>{STATUS_LABEL[run.status] ?? run.status}</span>
          </Link>
        ))}
      </div>
    </>
  );
}
